
import { mapActions } from 'vuex';
import { Component, Prop, Vue } from 'vue-property-decorator';
import renderChatbotMarkdown from '@/lib/chatbot-markdown';
import { ApiError } from '@/model/ApiError';
import {
    SheetChatContextMessage,
    SheetChatHistoryMessage,
    SheetChatRequest,
    SheetChatResponse,
} from '@/model/Sheet';

@Component({
    methods: {
        ...mapActions({
            sendChatMessage: 'sheetStorage/sendChatMessage',
        }),
    },
})
export default class AiChatbotWindow extends Vue {
    readonly maxHistoryRounds = 4;

    readonly rateLimitExceededMessageKey = 'chat.ai.rate-limit-exceeded';

    readonly supportEmail = 'zoltan.abry@abry.hu';

    readonly supportEmailHref = `mailto:${this.supportEmail}`;

    @Prop({ default: '' }) readonly sheetToken!: string;

    @Prop({ default: () => [] })
    readonly messages!: SheetChatHistoryMessage[];

    sendChatMessage!: (request: {
        sheetToken: string;
        message: SheetChatRequest;
    }) => Promise<SheetChatResponse>;

    message = '';

    loading = false;

    technicalError = '';

    serverValidationMessage = '';

    submitted = false;

    readonly maxMessageLength = 2000;

    get trimmedMessage(): string {
        return this.message.trim();
    }

    get isTooLong(): boolean {
        return this.message.length > this.maxMessageLength;
    }

    get validationMessage(): string {
        if (this.serverValidationMessage) {
            return this.serverValidationMessage;
        }

        if (!this.submitted) {
            return '';
        }

        if (!this.trimmedMessage) {
            return this.$t('sheet.chatbot.validation.required').toString();
        }

        if (this.isTooLong) {
            return this.$t('sheet.chatbot.validation.max-length', {
                max: this.maxMessageLength,
            }).toString();
        }

        return '';
    }

    get inputState(): boolean | null {
        return this.validationMessage ? false : null;
    }

    get isSubmitDisabled(): boolean {
        return (
            this.loading ||
            !this.sheetToken ||
            !this.trimmedMessage ||
            this.isTooLong
        );
    }

    updated(): void {
        this.scrollMessagesToBottom();
    }

    onInput(): void {
        this.serverValidationMessage = '';
    }

    async onSubmit(): Promise<void> {
        this.submitted = true;
        this.technicalError = '';
        this.serverValidationMessage = '';

        if (!this.sheetToken || this.validationMessage) {
            if (!this.sheetToken) {
                this.technicalError = this.$t(
                    'sheet.chatbot.technical-error',
                ).toString();
            }

            return;
        }

        const question = this.trimmedMessage;
        const previousMessages = [...this.messages];
        const messagesWithQuestion = [
            ...previousMessages,
            {
                id: this.getNextMessageId(),
                role: 'user',
                text: question,
            } as SheetChatHistoryMessage,
        ];

        this.$emit('messages-change', messagesWithQuestion);
        this.message = '';
        this.submitted = false;

        await this.$nextTick();

        this.loading = true;

        try {
            const response = await this.sendChatMessage({
                sheetToken: this.sheetToken,
                message: {
                    question,
                    history: this.buildHistory(previousMessages),
                },
            });

            this.$emit('messages-change', [
                ...messagesWithQuestion,
                {
                    id: this.getNextMessageId(messagesWithQuestion),
                    role: 'assistant',
                    text: response.answer,
                    status: response.status,
                } as SheetChatHistoryMessage,
            ]);
        } catch (error) {
            const apiError = error as ApiError;

            this.$emit('messages-change', previousMessages);

            if (apiError?.validationMessages?.question) {
                this.submitted = true;
                this.serverValidationMessage = String(
                    Array.isArray(apiError.validationMessages.question)
                        ? apiError.validationMessages.question[0]
                        : apiError.validationMessages.question,
                );
            } else {
                this.technicalError = this.getTechnicalErrorMessage(apiError);
            }
        } finally {
            this.loading = false;
        }
    }

    getTechnicalErrorMessage(apiError: ApiError): string {
        if (apiError?.key === this.rateLimitExceededMessageKey) {
            return this.$t(this.rateLimitExceededMessageKey).toString();
        }

        return this.$t('sheet.chatbot.technical-error').toString();
    }

    buildHistory(
        messages: SheetChatHistoryMessage[],
    ): SheetChatContextMessage[] {
        const completedRounds: SheetChatContextMessage[][] = [];
        let pendingUserMessage: SheetChatContextMessage | null = null;

        messages.forEach((message) => {
            if (
                message.status === 'ERROR' ||
                message.status === 'LIMIT_REACHED' ||
                message.status === 'DISABLED'
            ) {
                return;
            }

            if (message.role === 'user') {
                pendingUserMessage = {
                    role: 'user',
                    text: message.text,
                };
                return;
            }

            if (!pendingUserMessage) {
                return;
            }

            completedRounds.push([
                pendingUserMessage,
                {
                    role: 'assistant',
                    text: message.text,
                },
            ]);
            pendingUserMessage = null;
        });

        return completedRounds
            .slice(-this.maxHistoryRounds)
            .reduce(
                (history, round) => [...history, ...round],
                [] as SheetChatContextMessage[],
            );
    }

    isInsufficientInformationMessage(
        message: SheetChatHistoryMessage,
    ): boolean {
        return (
            message.role === 'assistant' &&
            message.status === 'INSUFFICIENT_INFORMATION'
        );
    }

    isLimitReachedMessage(message: SheetChatHistoryMessage): boolean {
        return (
            message.role === 'assistant' && message.status === 'LIMIT_REACHED'
        );
    }

    isDisabledMessage(message: SheetChatHistoryMessage): boolean {
        return message.role === 'assistant' && message.status === 'DISABLED';
    }

    isHighlightedAssistantMessage(message: SheetChatHistoryMessage): boolean {
        return (
            this.isInsufficientInformationMessage(message) ||
            this.isLimitReachedMessage(message) ||
            this.isDisabledMessage(message)
        );
    }

    getHistoryMessageText(message: SheetChatHistoryMessage): string {
        if (
            message.status === 'LIMIT_REACHED' ||
            message.status === 'DISABLED'
        ) {
            return this.$t(message.text).toString();
        }

        return message.text;
    }

    getInsufficientInformationTitle(): string {
        return this.$t('sheet.chatbot.insufficient-label').toString();
    }

    renderAssistantMessage(text: string): string {
        return renderChatbotMarkdown(text);
    }

    getNextMessageId(messages = this.messages): number {
        return (
            messages.reduce(
                (highestId, message) => Math.max(highestId, message.id),
                0,
            ) + 1
        );
    }

    scrollMessagesToBottom(): void {
        this.$nextTick(() => {
            const container = this.$refs.messagesContainer as HTMLElement;

            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }
}
